import React from 'react';
import {Tag, Typography} from 'antd';
import {useTranslation} from 'react-i18next';
import {PodExemptFlowType} from '../../graphql/podExempt/types';

interface Props {
  flows: PodExemptFlowType[];
  /** 字典 code → 展示名（驳回码），缺失时回退 code */
  dictNameOf?: (code: string) => string;
}

/** 审核环节配置：卡片标题 / 角色名 / 结论 tag 文案，按 action 前缀匹配。 */
const STAGES = [
  {
    prefix: 'FIRST',
    titleKey: 'podExempt.review.whsOpinion',
    roleKey: 'podExempt.review.whsRoleLabel',
    passKey: 'podExempt.review.passFirstTag',
    rejectKey: 'podExempt.review.rejectFirstTag',
  },
  {
    prefix: 'FINAL',
    titleKey: 'podExempt.review.centralOpinion',
    roleKey: 'podExempt.review.centralRoleLabel',
    passKey: 'podExempt.review.passFinalTag',
    rejectKey: 'podExempt.review.rejectFinalTag',
  },
] as const;

/** 取该环节最近一条流水（flows 为时间升序，从尾部找）；只展示最近一轮，历史轮次去流转记录看。 */
const latestOf = (flows: PodExemptFlowType[], prefix: string): PodExemptFlowType | null =>
  [...flows]
    .reverse()
    .find((flow) => flow.action === `${prefix}_PASS` || flow.action === `${prefix}_REJECT`) ?? null;

/** 是否有可展示的审核意见（调用方据此决定要不要留出分区间距）。 */
export const hasOpinionCards = (flows: PodExemptFlowType[]): boolean =>
  STAGES.some((stage) => latestOf(flows, stage.prefix) !== null);

/**
 * 审核意见卡（仓管初审 / 中台终审各一张）：DSP 抽屉与初审 / 终审页共用，位置统一在上报信息之后。
 *
 * <p>数据源只认 pod_exempt_flow：带操作人与时间，且有几个环节就出几张卡。报表冗余字段
 * latestRejectCodes / latestRejectOpinion 不再单独渲染，避免同一条驳回在页面上出现两次。
 */
const PodExemptOpinionCards: React.FC<Props> = ({flows, dictNameOf}) => {
  const {t} = useTranslation();
  const nameOf = dictNameOf ?? ((code: string) => code);

  const cards = STAGES.map((stage) => ({stage, flow: latestOf(flows, stage.prefix)})).filter(
    (item): item is {stage: (typeof STAGES)[number]; flow: PodExemptFlowType} => item.flow !== null,
  );
  if (cards.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      {cards.map(({stage, flow}) => {
        const passed = flow.action.endsWith('_PASS');
        return (
          // 原型样式：紫色左边条卡，标题+结论 tag，右侧 角色·姓名·时间，正文为意见原文
          <div
            key={stage.prefix}
            className="bg-white rounded-lg border border-gray-100 border-l-4 border-l-purple-500 shadow-sm px-5 py-4"
          >
            <div className="flex justify-between items-center mb-3 gap-3">
              <span className="flex items-center gap-2 min-w-0">
                <span className="text-sm font-semibold whitespace-nowrap">{t(stage.titleKey)}</span>
                <Tag color={passed ? 'green' : 'red'}>
                  {t(passed ? stage.passKey : stage.rejectKey)}
                </Tag>
              </span>
              <span className="text-xs text-gray-400 whitespace-nowrap">
                {[
                  t(stage.roleKey),
                  flow.operatorName,
                  flow.createdAt ? new Date(flow.createdAt).toLocaleString() : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>
            {flow.rejectCodes.length > 0 && (
              // 驳回原因逐条竖排（模板文案长，横排换行观感差）
              <div className="mb-2 flex flex-col items-start gap-1.5">
                {flow.rejectCodes.map((code) => (
                  <Tag key={code} color="error" className="mr-0">
                    {nameOf(code)}
                  </Tag>
                ))}
              </div>
            )}
            <div className="text-sm whitespace-pre-wrap leading-relaxed">
              {flow.opinion ? (
                <Typography.Text type={passed ? undefined : 'danger'}>{flow.opinion}</Typography.Text>
              ) : (
                <span className="text-gray-400">-</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PodExemptOpinionCards;
